import {
  ACCOUNT_TYPE_DEFINITIONS,
  DISPOSITION_DEFINITIONS,
  GC_STATUS_DEFINITIONS,
  PRIMARY_USE_DEFINITIONS,
  PROJECT_ACCOUNT_ROLE_DEFINITIONS,
  PROJECT_STAGE_DEFINITIONS,
  ROLE_FAMILY_DEFINITIONS,
  SIGNAL_TYPE_DEFINITIONS,
} from '@parcel/types';
import type { WhereField } from '../types.js';
import { REVIEW_PATH_OPTIONS, STRUCTURE_OPTIONS } from '../vocab.js';
import { fieldsIn, workspaceFields } from './shared.js';

const stages = PROJECT_STAGE_DEFINITIONS.map((d) => ({
  value: d.value,
  label: d.label,
  description: d.description,
}));
const f = fieldsIn('firm');
const p = fieldsIn('project', 'project');
const c = fieldsIn('contact', 'contact');
const s = fieldsIn('signals', 'signal');

export const ACCOUNT_FIELDS: readonly WhereField[] = [
  f('name', 'Name', 'text', 'Firm name or any name it goes by.'),
  f('type', 'Type', 'choice', 'What the firm does. Other means no type is known yet.', {
    options: ACCOUNT_TYPE_DEFINITIONS,
  }),
  f('city', 'City', 'choice', "City of the firm's office.", { source: 'firm_cities' }),
  f('state', 'State', 'text', 'Two-letter state code of the firm.'),
  f('specialties', 'Specialties', 'text', "The firm's stated specialties."),
  f('description', 'Description', 'text', 'Plain-English description of the firm.'),
  f('founded', 'Year founded', 'number', 'Year the firm was founded.', { unit: 'year' }),
  f('has_website', 'Has website', 'yes_no', 'Whether Parcel knows the firm website.'),
  f('has_linkedin', 'Has LinkedIn', 'yes_no', 'Whether Parcel knows the firm LinkedIn page.'),
  f('has_phone', 'Has phone', 'yes_no', 'Whether the firm has a public phone number.'),
  f('has_email', 'Has email', 'yes_no', 'Whether the firm has a public email address.'),
  f(
    'projects_count',
    'Number of projects',
    'number',
    'Projects the firm is linked to, in any role.',
    { unit: 'count' },
  ),
  f('contacts_count', 'Number of contacts', 'number', 'People Parcel knows at the firm.', {
    unit: 'count',
  }),
  f('added', 'Added to Parcel', 'date', 'When the firm entered Parcel.'),

  p('role', 'Role on the project', 'choice', "The firm's role on the project.", {
    options: PROJECT_ACCOUNT_ROLE_DEFINITIONS,
  }),
  p('city', 'City', 'choice', 'City of the project.', { source: 'cities' }),
  p('neighborhood', 'Neighborhood', 'choice', 'Neighborhood of the project.', {
    source: 'neighborhoods',
  }),
  p('stage', 'Stage', 'choice', 'Development stage of the project.', { options: stages }),
  p('status', 'Status', 'choice', 'Whether the project is active or reached an outcome.', {
    options: DISPOSITION_DEFINITIONS,
  }),
  p('use', 'Use', 'choice', 'Primary use of the project.', { options: PRIMARY_USE_DEFINITIONS }),
  p('units', 'Units', 'number', 'Dwelling units.', { unit: 'units' }),
  p('sqft', 'Square footage', 'number', 'Gross floor area.', { unit: 'sqft' }),
  p('stories', 'Stories', 'number', 'Stories above grade.'),
  p('cost', 'Construction cost', 'number', 'Construction cost in US dollars.', { unit: 'usd' }),
  p('year_completed', 'Year completed', 'number', 'Year the project completed or is expected to.', {
    unit: 'year',
  }),
  p('gc', 'General contractor', 'choice', 'Whether a general contractor is named.', {
    options: GC_STATUS_DEFINITIONS,
  }),
  p('structure', 'Structure', 'choice', 'How the building is built.', {
    options: STRUCTURE_OPTIONS,
  }),
  p('review_path', 'Review path', 'choice', 'Zoning and review paths the project follows.', {
    options: REVIEW_PATH_OPTIONS,
  }),
  p('mixed_use', 'Mixed use', 'yes_no', 'A mix of residential and commercial uses.'),
  p('senior', 'Senior housing', 'yes_no', 'Age-restricted housing.'),
  p('as_of_right', 'As of right', 'yes_no', 'Needs no discretionary zoning relief.'),
  p('redevelopment', 'Redevelopment', 'yes_no', 'Replaces or reuses existing buildings.'),
  p('stalled', 'Stalled', 'yes_no', 'The project has stalled.'),
  p('last_signal', 'Last signal', 'date', 'Date of the newest signal on the project.'),

  c('title', 'Title', 'text', 'Job title of someone at the firm.'),
  c('persona', 'Persona', 'choice', 'Role family the title maps to.', {
    options: ROLE_FAMILY_DEFINITIONS,
  }),
  c('has_email', 'Has email', 'yes_no', 'Parcel knows their work email.'),
  c('has_phone', 'Has phone', 'yes_no', 'Parcel knows their phone.'),

  s('type', 'Type', 'choice', 'Kind of event on one of their projects.', {
    options: SIGNAL_TYPE_DEFINITIONS,
  }),
  s('filed', 'Filed date', 'date', 'When the event happened.'),

  fieldsIn('scores')('score', 'ICP Score', 'score', "This workspace's ICP Score, 0 to 100."),
  ...workspaceFields('account'),
];
